import React, { useState } from 'react';
import { parseLTVFromCSV, parseLTVFromPDF } from '../utils/ltvParser';
import { localApi } from '../utils/localApi';

interface LTV {
    id: string;
    startPK: number;
    endPK: number;
    speed: number;
    reason: string;
    startDate: string;
    endDate: string;
    line?: 'Centrale' | 'Balagne';
}

interface SlowDownManagerProps {
    line: 'Centrale' | 'Balagne';
    ltvList: LTV[];
    onRefresh?: () => void;
}

export default function SlowDownManager({ line, ltvList, onRefresh }: SlowDownManagerProps) {
    const [activeTab, setActiveTab] = useState<'manual' | 'csv'>('manual');
    const [isDragging, setIsDragging] = useState(false);

    // Form States
    const [startPK, setStartPK] = useState('');
    const [endPK, setEndPK] = useState('');
    const [speed, setSpeed] = useState('40');
    const [reason, setReason] = useState('Travaux Voie');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newLTV = {
            id: Date.now().toString(),
            startPK: Number(startPK),
            endPK: Number(endPK),
            speed: Number(speed),
            reason,
            startDate,
            endDate,
            line
        };

        const res = await localApi.createLtv(newLTV);
        if (res.error) {
            console.error("Erreur insertion manuel:", res.error);
            alert("Erreur réseau lors de l'enregistrement de la zone.");
            return;
        }

        // Reset form
        setStartPK(''); setEndPK('');
        onRefresh?.();
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            let ltvs: any[] = [];
            const isPdf = file.name.toLowerCase().endsWith('.pdf');
            
            if (isPdf) {
                const serverRes = await localApi.convertLtvPdf(file);
                if (serverRes && serverRes.success && Array.isArray(serverRes.ltvs) && serverRes.ltvs.length > 0) {
                    ltvs = serverRes.ltvs;
                } else {
                    console.warn("Réponse serveur sans LTV, tentative analyse locale:", serverRes);
                    ltvs = await parseLTVFromPDF(file);
                }
            } else {
                ltvs = await parseLTVFromCSV(file);
            }

            console.log("Parse LTV Succes:", ltvs);
            if (ltvs.length === 0) {
                alert(`Aucun ralentissement n'a pu être extrait. Vérifiez que le fichier ${isPdf ? 'PDF' : 'CSV'} est au bon format.`);
                return;
            }

            const formattedLTVs = ltvs.map(l => ({
                id: l.id || Date.now().toString() + Math.random(),
                startPK: l.startPK,
                endPK: l.endPK,
                speed: l.speed,
                reason: l.reason,
                startDate: l.startDate,
                endDate: l.endDate,
                line
            }));

            const res = await localApi.importLtvs(formattedLTVs);
            if (res.error) {
                console.error("Erreur insertion LTV:", res.error);
                alert("Erreur lors de la sauvegarde du fichier.");
                return;
            }

            alert(`${ltvs.length} zone(s) de ralentissement synchronisées depuis le document avec succès pour la ligne ${line} !`);
            onRefresh?.();
        } catch (error: any) {
            console.error("Erreur parsing LTV: ", error);
            alert("Une erreur est survenue lors de la lecture du fichier : " + (error?.message || error));
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (!file || (!file.name.endsWith('.csv') && !file.name.endsWith('.pdf'))) {
            alert("Veuillez déposer un fichier .csv ou .pdf valide.");
            return;
        }

        // Réutiliser la logique de téléchargement, on simule un event
        const synthEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleFileUpload(synthEvent);
    };

    return (
        <div className="bg-slate-900/60 backdrop-blur-md border border-white/5 rounded-3xl h-full flex flex-col shadow-2xl relative overflow-hidden">
            {/* Background Effects */}
            <div className={`absolute top-0 right-0 w-[500px] h-[500px] ${line === 'Balagne' ? 'bg-rose-900/20' : 'bg-cyan-900/20'} blur-[100px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2`} />
            
            <div className="p-6 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
                <div>
                    <h3 className="text-white font-black uppercase text-xl sm:text-2xl italic tracking-tighter flex items-center gap-3">
                        <span className={`w-10 h-10 rounded-2xl ${line === 'Balagne' ? 'bg-rose-500/10 border-rose-500/20' : 'bg-cyan-500/10 border-cyan-500/20'} flex items-center justify-center border shrink-0`}>
                           <svg className={`w-5 h-5 ${line === 'Balagne' ? 'text-rose-500' : 'text-cyan-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                        </span>
                        Gestion LTV <span className={line === 'Balagne' ? 'text-rose-500' : 'text-cyan-500'}>{line}</span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-2">Limitations Temporaires de Vitesse - Ligne {line}</p>
                </div>
                
                {/* Tabs */}
                <div className="flex bg-slate-950 p-1.5 rounded-2xl border border-white/5 shadow-inner self-start sm:self-auto">
                    <button
                        onClick={() => setActiveTab('manual')}
                        className={`px-4 sm:px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                            activeTab === 'manual' 
                                ? (line === 'Balagne' ? 'bg-rose-500 text-white shadow-lg shadow-rose-900/40' : 'bg-cyan-500 text-white shadow-lg shadow-cyan-900/40') 
                                : 'text-slate-500 hover:text-white'
                        }`}
                    >
                        Saisie Manuelle
                    </button>
                    <button
                        onClick={() => setActiveTab('csv')}
                        className={`px-4 sm:px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${
                            activeTab === 'csv' 
                                ? (line === 'Balagne' ? 'bg-rose-500 text-white shadow-lg shadow-rose-900/40' : 'bg-cyan-500 text-white shadow-lg shadow-cyan-900/40') 
                                : 'text-slate-500 hover:text-white'
                        }`}
                    >
                        Import CSV/PDF
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-8 relative z-10">
                
                {/* Left Column: Form / Upload */}
                <div className="flex-[1] min-w-[280px] sm:min-w-[300px]">

                {/* Manual Form */}
                {activeTab === 'manual' && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={`block text-[9px] ${line === 'Balagne' ? 'text-rose-500' : 'text-cyan-500'} font-bold uppercase mb-2`}>PK Début</label>
                                <input required type="number" step="0.1" value={startPK} onChange={(e) => setStartPK(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white text-xs" placeholder="ex: 119.5" />
                            </div>
                            <div>
                                <label className={`block text-[9px] ${line === 'Balagne' ? 'text-rose-500' : 'text-cyan-500'} font-bold uppercase mb-2`}>PK Fin</label>
                                <input required type="number" step="0.1" value={endPK} onChange={(e) => setEndPK(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white text-xs" placeholder="ex: 118.0" />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-[9px] ${line === 'Balagne' ? 'text-rose-500' : 'text-cyan-500'} font-bold uppercase mb-2`}>Vitesse (Km/h)</label>
                            <input required type="number" value={speed} onChange={(e) => setSpeed(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white text-xs font-mono" />
                        </div>

                        <div>
                            <label className={`block text-[9px] ${line === 'Balagne' ? 'text-rose-500' : 'text-cyan-500'} font-bold uppercase mb-2`}>Motif</label>
                            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-white text-xs">
                                <option>Travaux Voie</option>
                                <option>Ouvrage d'Art</option>
                                <option>Glissement Terrain</option>
                                <option>Végétation</option>
                                <option>Autre</option>
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[9px] text-slate-500 font-bold uppercase mb-2">Date Début</label>
                                <input required type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-2 text-white text-[10px]" />
                            </div>
                            <div>
                                <label className="block text-[9px] text-slate-500 font-bold uppercase mb-2">Date Fin</label>
                                <input required type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl p-2 text-white text-[10px]" />
                            </div>
                        </div>

                        <button type="submit" className={`w-full ${line === 'Balagne' ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-900/40' : 'bg-cyan-600 hover:bg-cyan-500 shadow-cyan-900/40'} text-white font-black uppercase text-[10px] tracking-widest py-4 rounded-xl shadow-lg transition-transform active:scale-95`}>
                            Ajouter la LTV ({line})
                        </button>
                    </form>
                )}

                {/* CSV/PDF Upload */}
                {activeTab === 'csv' && (
                    <div
                        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer relative ${
                            isDragging 
                                ? (line === 'Balagne' ? 'border-rose-500 bg-rose-500/10' : 'border-cyan-500 bg-cyan-500/10') 
                                : (line === 'Balagne' ? 'border-white/10 hover:border-rose-500/50' : 'border-white/10 hover:border-cyan-500/50')
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <input type="file" accept=".csv,.pdf" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" title=" " />
                        <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl pointer-events-none">📄</div>
                        <p className="text-xs text-white font-bold mb-2 pointer-events-none">Déposer fichier CSV ou PDF ici</p>
                        <p className="text-[10px] text-slate-500 pointer-events-none">Format CSV (PK Début, PK Fin, Vitesse) ou PDF officiel d'ASS</p>
                    </div>
                )}
                </div>

                {/* Right Column: List */}
                <div className="flex-[2] flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest flex items-center gap-2">
                           <span className={`w-2 h-2 rounded-full ${line === 'Balagne' ? 'bg-rose-500' : 'bg-cyan-500'} inline-block animate-pulse`}></span>
                           Zones Actives en Circulation
                        </p>
                        <span className={`bg-slate-800 ${line === 'Balagne' ? 'text-rose-400' : 'text-cyan-400'} px-3 py-1 rounded-full text-xs font-bold border border-white/5`}>
                           {ltvList.length} Totale(s)
                        </span>
                    </div>
                    
                    {ltvList.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-3xl p-8 text-center bg-slate-800/20">
                           <div className="w-16 h-16 bg-slate-800 rounded-[2rem] flex items-center justify-center mb-4 text-3xl opacity-50 shadow-inner">🟢</div>
                           <h4 className="text-white font-bold opacity-80 mb-2">Aucune restriction ({line})</h4>
                           <p className="text-sm text-slate-500 max-w-sm">Le réseau est actuellement nominal. Il n'y a aucune limitation temporaire de vitesse active sur la ligne {line}.</p>
                        </div>
                    ) : (
                        <div className="space-y-3 overflow-y-auto pr-2 pb-4">
                            {ltvList.map(ltv => {
                                const speedNum = Number(ltv.speed);
                                const isYellowFlag = isNaN(speedNum) || speedNum === 0;

                                return (
                                <div key={ltv.id} className={`bg-slate-800/40 border ${isYellowFlag ? 'border-yellow-500/20 hover:border-yellow-500/50' : 'border-white/5 hover:border-red-500/30'} p-5 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:bg-slate-800/60 transition-all shadow-lg`}>
                                    <div className="flex items-start gap-4">
                                        <div className={`w-12 h-12 rounded-2xl ${isYellowFlag ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/20'} border flex flex-col items-center justify-center shrink-0`}>
                                            <span className={`text-[8px] font-black uppercase ${isYellowFlag ? 'text-yellow-500/80' : 'text-red-500/70'} tracking-tighter`}>{isYellowFlag ? 'ATTENTION' : 'MAX'}</span>
                                            <span className={`text-sm font-black ${isYellowFlag ? 'text-yellow-400' : 'text-red-400'} font-mono tracking-tighter`}>{isYellowFlag ? '🟡' : ltv.speed}</span>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <span className="text-sm font-black text-white">PK {ltv.startPK} <span className="text-slate-500 font-normal mx-1">à</span> PK {ltv.endPK}</span>
                                                {isYellowFlag && (
                                                    <span className="text-[10px] font-bold text-yellow-300 bg-yellow-950/60 px-2 py-0.5 rounded border border-yellow-500/30 animate-pulse">
                                                        Drapeau Jaune
                                                    </span>
                                                )}
                                                {ltv.id && !ltv.id.includes('.') && (
                                                    <span className="text-[10px] font-bold text-cyan-400 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/20">
                                                        ASS: {ltv.id.split('-')[0]}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-400 flex flex-wrap items-center gap-2">
                                                <span className={`w-1.5 h-1.5 rounded-full ${isYellowFlag ? 'bg-yellow-500' : 'bg-slate-500'}`}></span>
                                                {ltv.reason}
                                                {(ltv.startDate || ltv.endDate) && (
                                                    <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-widest bg-black/40 px-2 py-0.5 rounded-md border border-white/5 flex items-center gap-1">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                        {ltv.endDate ? `Jusqu'au ${new Date(ltv.endDate).toLocaleDateString()}` : `Dès le ${new Date(ltv.startDate).toLocaleDateString()}`}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={async () => {
                                        if (confirm(`Êtes-vous sûr de vouloir supprimer la LTV du PK ${ltv.startPK} à ${ltv.endPK} ?`)) {
                                            const res = await localApi.deleteLtv(ltv.id);
                                            if (res.error) {
                                                console.error("Erreur suppression:", res.error);
                                                alert("Erreur lors de la suppression de la zone.");
                                            } else {
                                                onRefresh?.();
                                            }
                                        }
                                    }} className="w-10 h-10 rounded-xl bg-red-500/10 text-red-400 sm:opacity-0 group-hover:opacity-100 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all scale-95 hover:scale-100 shrink-0 self-end sm:self-auto">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
